import type { LeadId, PipelineId, StatusId } from '../types/ids.ts'
import { LEAD_EDITABLE_FIELDS, type LeadEditableField } from '../events/index.ts'

/**
 * Typed error taxonomy for CRM adapters. The SBO-26 DoD requires "no bare
 * `Error`" — adapter methods throw one of these instead so callers can branch
 * on `instanceof` and the engine's queue consumer can route failures into the
 * right retry / dead-letter / audit channel.
 *
 * Concrete adapters in `services/integrations-*` reuse the same set; they
 * don't define their own. If a new failure mode emerges in a real adapter,
 * add it here so the shape stays uniform.
 *
 * **Class hierarchy here is intentional and confined to control-flow.** Each
 * class is `extends Error` with typed payload fields; none model business
 * logic. The root CLAUDE.md rule "no classes outside the CRM Adapter family"
 * targets functional-vs-OOP modelling of *domain*, which these are not.
 *
 * **Branded-id usage.** Where the payload value was already a validated
 * branded id at the throw site (e.g. a `LeadId` parameter), the field is
 * typed as the brand. Where the payload value is a raw external reference
 * (e.g. `managerExternalRef` from `BaseCrmAdapter` or a `leads.external_ref`
 * column value), the field is typed as `string`. Casting a raw external ref
 * to a brand at the throw site would be a type lie — the brand-id rule
 * exists precisely to prevent that mix-up between unrelated id namespaces.
 */

export class LeadNotFoundError extends Error {
  readonly leadId: LeadId | null
  readonly externalRef: string | null

  constructor(ref: { leadId: LeadId } | { externalRef: string }) {
    const desc = 'leadId' in ref ? `id=${ref.leadId}` : `external_ref=${ref.externalRef}`
    super(`Lead not found: ${desc}`)
    this.name = 'LeadNotFoundError'
    this.leadId = 'leadId' in ref ? ref.leadId : null
    this.externalRef = 'externalRef' in ref ? ref.externalRef : null
  }
}

export class ManagerNotFoundError extends Error {
  readonly managerRef: string

  constructor(managerRef: string) {
    super(`Manager not found: ${managerRef}`)
    this.name = 'ManagerNotFoundError'
    this.managerRef = managerRef
  }
}

export class InternalStatusNotFoundError extends Error {
  readonly statusId: StatusId
  readonly pipelineId: PipelineId

  constructor(statusId: StatusId, pipelineId: PipelineId) {
    super(`Internal status not found: ${statusId} (pipeline ${pipelineId})`)
    this.name = 'InternalStatusNotFoundError'
    this.statusId = statusId
    this.pipelineId = pipelineId
  }
}

export class PipelineNotFoundError extends Error {
  readonly pipelineId: PipelineId

  constructor(pipelineId: PipelineId) {
    super(`Pipeline not found: ${pipelineId}`)
    this.name = 'PipelineNotFoundError'
    this.pipelineId = pipelineId
  }
}

export class InvalidEventPayloadError extends Error {
  readonly kind: string
  readonly reason: string

  constructor(kind: string, reason: string) {
    super(`Invalid payload for event kind '${kind}': ${reason}`)
    this.name = 'InvalidEventPayloadError'
    this.kind = kind
    this.reason = reason
  }
}

/**
 * Deactivating a status that still hosts live leads without naming a migration
 * target. Carries the live-lead count so surfaces can say "move N leads first"
 * (routes map it to HTTP 409).
 */
export class StatusInUseError extends Error {
  readonly statusId: StatusId
  readonly pipelineId: PipelineId
  readonly leadCount: number

  constructor(statusId: StatusId, pipelineId: PipelineId, leadCount: number) {
    super(
      `Status ${statusId} (pipeline ${pipelineId}) still holds ${leadCount} live lead(s); supply a migration target to deactivate it`,
    )
    this.name = 'StatusInUseError'
    this.statusId = statusId
    this.pipelineId = pipelineId
    this.leadCount = leadCount
  }
}

/**
 * Split by what the caller should do next rather than by HTTP status: a
 * throttled call is worth retrying next cycle and a rejected credential never
 * is, which is what lets a poller back off instead of hammering (ADR-0004 §4).
 * `not_found` is not "empty" — a private source answers it for denied access too.
 */
export type SourceFailureKind = 'rejected' | 'throttled' | 'not_found' | 'timeout' | 'unavailable'

/**
 * Lives here, not in an adapter: every `services/integrations-*` hits the same
 * five walls and the engine branches on them identically. `status` is `null`
 * when no response arrived at all.
 */
export class SourceRequestError extends Error {
  readonly kind: SourceFailureKind
  readonly status: number | null

  constructor(kind: SourceFailureKind, message: string, status: number | null = null) {
    super(message)
    this.name = 'SourceRequestError'
    this.kind = kind
    this.status = status
  }
}

/**
 * How an engine→adapter call failed, spelled as ADR-0001 §3 names it. These are
 * the *reason* half of a `pipeline.<reason>` event type — see
 * `adapterFailureEventType` in `../events/index.ts`, which is the one place the
 * translation happens.
 *
 * They are deliberately NOT dotted to match the event vocabulary, because the
 * same four strings are simultaneously `AdapterProxyError`'s discriminator, the
 * input to `retryable` below, and the text a route puts on the wire for an
 * operator to read on a 502. Only the event type is dotted. Adding a fifth
 * reason is a compile error in `../events/index.ts` until it is listed there.
 *
 * Distinct from `SourceFailureKind`, which describes the *adapter→source* hop.
 * A GitHub timeout and a timeout reaching the Worker that talks to GitHub are
 * different outages with different fixes.
 */
export type AdapterFailureReason =
  | 'adapter_timeout'
  | 'adapter_error'
  | 'adapter_protocol_error'
  | 'adapter_unreachable'

/**
 * A call that never produced an answer the contract can interpret. An adapter
 * that answers 501 or 500 *did* answer — those cross back as
 * `UnsupportedOperationError` and a plain `Error` respectively (ADR-0001 §2).
 */
export class AdapterProxyError extends Error {
  readonly reason: AdapterFailureReason
  readonly status: number | null
  /** A malformed answer is the one failure a retry cannot fix (ADR-0001 §3). */
  readonly retryable: boolean

  constructor(reason: AdapterFailureReason, message: string, status: number | null = null) {
    super(message)
    this.name = 'AdapterProxyError'
    this.reason = reason
    this.status = status
    this.retryable = reason !== 'adapter_protocol_error'
  }
}

export type BoardWrite = 'create' | 'moveStage' | 'reassign' | LeadEditableField
export const BOARD_WRITES: readonly BoardWrite[] = [
  'create',
  'moveStage',
  'reassign',
  ...LEAD_EDITABLE_FIELDS,
]

export class WriteNotCarriedError extends Error {
  readonly write: BoardWrite
  readonly adapterKind: string
  readonly code = 'write_not_carried'

  constructor(write: BoardWrite, adapterKind: string) {
    super(`Write not carried back to '${adapterKind}': ${write}`)
    this.name = 'WriteNotCarriedError'
    this.write = write
    this.adapterKind = adapterKind
  }
}
